import vanillaJson from '@/data/vanillaItems.json';
import type { ItemCategory, ItemId, ItemMeta } from '@/types/domain';

const VANILLA = vanillaJson as ItemMeta[];
const BY_ID = new Map<ItemId, ItemMeta>(VANILLA.map((i) => [i.id, i]));

/**
 * Registry of vanilla item metadata. Custom (AI-generated) items are merged in at
 * runtime via `register`, so lookups see the whole inventory uniformly.
 */
export class VanillaItemRegistry {
  private custom = new Map<ItemId, ItemMeta>();
  private customOrder: ItemMeta[] = [];

  allVanilla(): ItemMeta[] {
    return VANILLA;
  }

  register(item: ItemMeta): void {
    this.custom.set(item.id, item);
    if (!this.customOrder.find((i) => i.id === item.id)) this.customOrder.push(item);
  }

  unregister(id: ItemId): void {
    this.custom.delete(id);
    this.customOrder = this.customOrder.filter((i) => i.id !== id);
  }

  allCustom(): ItemMeta[] {
    return this.customOrder;
  }

  get(id: ItemId): ItemMeta | undefined {
    return this.custom.get(id) ?? BY_ID.get(id);
  }

  has(id: ItemId): boolean {
    return this.custom.has(id) || BY_ID.has(id);
  }

  search(query: string, category?: ItemCategory | 'all', limit = 500): ItemMeta[] {
    const q = query.trim().toLowerCase();
    const base = VANILLA;
    const filtered = base.filter((i) => {
      if (category && category !== 'all' && i.category !== category) return false;
      if (!q) return true;
      return (
        i.id.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q) ||
        (i.aliases || []).some((a) => a.includes(q)) ||
        i.tags.some((t) => t.includes(q))
      );
    });
    return filtered.slice(0, limit);
  }

  get count(): number {
    return VANILLA.length;
  }
}

export const itemRegistry = new VanillaItemRegistry();
